const COMMANDS = {
    h1: (c) => c.toggleHeading({ level: 1 }),
    h2: (c) => c.toggleHeading({ level: 2 }),
    h3: (c) => c.toggleHeading({ level: 3 }),
    bold: (c) => c.toggleBold(),
    italic: (c) => c.toggleItalic(),
    underline: (c) => c.toggleUnderline(),
    strike: (c) => c.toggleStrike(),
    code: (c) => c.toggleCode(),
    bullet: (c) => c.toggleBulletList(),
    ordered: (c) => c.toggleOrderedList(),
    tasks: (c) => c.toggleTaskList(),
    blockquote: (c) => c.toggleBlockquote(),
    codeblock: (c) => c.toggleCodeBlock(),
    'align-left': (c) => c.setTextAlign('left'),
    'align-center': (c) => c.setTextAlign('center'),
    'align-right': (c) => c.setTextAlign('right'),
    undo: (c) => c.undo(),
    redo: (c) => c.redo(),
};

const ACTIVE = {
    h1: ['heading', { level: 1 }],
    h2: ['heading', { level: 2 }],
    h3: ['heading', { level: 3 }],
    bold: ['bold'],
    italic: ['italic'],
    underline: ['underline'],
    strike: ['strike'],
    code: ['code'],
    bullet: ['bulletList'],
    ordered: ['orderedList'],
    tasks: ['taskList'],
    blockquote: ['blockquote'],
    codeblock: ['codeBlock'],
    'align-left': [{ textAlign: 'left' }],
    'align-center': [{ textAlign: 'center' }],
    'align-right': [{ textAlign: 'right' }],
};

const normalize = (html) => (html === '<p></p>' ? '' : html);

/**
 * <mellow-editor>: loads the TipTap bundle on demand and wires the toolbar.
 * `.value` is the HTML ("" when empty); updates fire `input` for wire:model.
 */
export class MellowEditor extends HTMLElement {
    connectedCallback() {
        if (this._ready) return;
        this._ready = true;

        this._value = this.querySelector('template[data-mellow-editor-initial]')?.innerHTML.trim() ?? '';

        this.toolbar?.addEventListener('click', (event) => {
            const button = event.target.closest('[data-command]');
            if (button && !button.disabled) this.run(button.dataset.command, button);
        });
        this.toolbar?.addEventListener('keydown', this.onToolbarKeydown);
        // Clicking a tool must not move focus (and the selection) out of the text.
        this.toolbar?.addEventListener('pointerdown', (event) => {
            if (event.target.closest('[data-command]:not([data-command="link"])')) event.preventDefault();
        });

        this.load();
    }

    disconnectedCallback() {
        this.editor?.destroy();
        this.editor = null;
        this._ready = false;
    }

    get toolbar() {
        return this.querySelector('.mellow-editor-toolbar');
    }

    get buttons() {
        return [...(this.toolbar?.querySelectorAll('[data-command]') ?? [])];
    }

    get value() {
        return this.editor ? normalize(this.editor.getHTML()) : this._value;
    }

    set value(html) {
        // While the user is typing, a (debounced) Livewire response carries an older state:
        // applying it would drop keystrokes and reset the cursor. The editor is the source of truth then.
        if (this.editor?.isFocused) return;

        this._value = html ?? '';
        if (this.editor && normalize(this.editor.getHTML()) !== this._value) {
            this.editor.commands.setContent(this._value, { emitUpdate: false });
        }
        this.syncHidden();
    }

    async load() {
        const mount = this.querySelector('.mellow-editor-content');
        const { createEditor } = await import(this.dataset.src);
        if (!this.isConnected || this.editor) return;

        // A page restored from Livewire's navigation cache still holds the previous editor's
        // DOM: continue with its content and mount a fresh editor in its place.
        const stale = mount.querySelector('.ProseMirror');
        if (stale) {
            this._value = normalize(stale.innerHTML);
            mount.replaceChildren();
        }

        let attributes = {};
        try {
            attributes = JSON.parse(mount.dataset.aria || '{}');
        } catch {}
        if (mount.dataset.id) attributes.id = mount.dataset.id;

        this.editor = createEditor({
            element: mount,
            content: this._value,
            placeholder: this.dataset.placeholder ?? '',
            taskLabel: this.dataset.taskLabel ?? '',
            attributes,
            onUpdate: (editor) => {
                this._value = normalize(editor.getHTML());
                this.syncHidden();
                this.dispatchEvent(new CustomEvent('input', { bubbles: true, detail: this._value }));
                this.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: this._value }));
            },
            onTransaction: () => this.syncToolbar(),
        });

        this.buttons.forEach((button) => (button.disabled = false));
        this.syncToolbar();
        this.dispatchEvent(new CustomEvent('mellow:editor-ready', { bubbles: true, detail: this.editor }));
    }

    run(command, button) {
        if (!this.editor) return;
        if (command === 'link') return this.editLink(button);

        const chain = this.editor.chain().focus();
        COMMANDS[command]?.(chain).run();
    }

    syncToolbar() {
        if (!this.editor) return;
        for (const button of this.buttons) {
            const active = ACTIVE[button.dataset.command];
            if (active) button.setAttribute('aria-pressed', String(this.editor.isActive(...active)));
            if (button.dataset.command === 'undo') button.disabled = !this.editor.can().undo();
            if (button.dataset.command === 'redo') button.disabled = !this.editor.can().redo();
            if (button.dataset.command === 'link') button.toggleAttribute('data-active', this.editor.isActive('link'));
        }
    }

    syncHidden() {
        const hidden = this.querySelector('[data-mellow-editor-value]');
        if (hidden && hidden.value !== this._value) hidden.value = this._value;
    }

    /** A small popover to add, change or remove a link. */
    editLink(button) {
        let panel = this.querySelector('.mellow-editor-link');
        if (!panel) {
            panel = document.createElement('form');
            panel.className = 'mellow-editor-link mellow-popover';
            panel.setAttribute('popover', '');
            panel.innerHTML = `
                <div class="mellow-input" data-size="sm" data-variant="outline"><input class="mellow-input-control" type="url" required></div>
                <button type="submit" class="mellow-button" data-variant="primary" data-size="sm"></button>
                <button type="button" class="mellow-button" data-variant="ghost" data-size="sm" data-unlink></button>`;
            const input = panel.querySelector('input');
            input.setAttribute('aria-label', this.dataset.linkLabel ?? 'URL');
            input.placeholder = 'https://';
            panel.querySelector('[type="submit"]').textContent = this.dataset.linkApply ?? 'Apply';
            panel.querySelector('[data-unlink]').textContent = this.dataset.linkRemove ?? 'Remove';

            panel.addEventListener('submit', (event) => {
                event.preventDefault();
                this.editor.chain().focus().extendMarkRange('link').setLink({ href: input.value.trim() }).run();
                panel.hidePopover();
            });
            panel.querySelector('[data-unlink]').addEventListener('click', () => {
                this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
                panel.hidePopover();
            });
            // Keep the editor's selection while typing into the link field.
            panel.addEventListener('input', (event) => event.stopPropagation());
            this.append(panel);
        }

        panel.querySelector('input').value = this.editor.getAttributes('link').href ?? '';
        panel.querySelector('[data-unlink]').hidden = !this.editor.isActive('link');
        panel.showPopover();

        const rect = button.getBoundingClientRect();
        panel.style.left = `${Math.min(rect.left, window.innerWidth - panel.offsetWidth - 8)}px`;
        panel.style.top = `${rect.bottom + 4}px`;
        panel.querySelector('input').focus();
    }

    /** WAI-ARIA toolbar: one tab stop, arrow keys move between buttons. */
    onToolbarKeydown = (event) => {
        const buttons = this.buttons.filter((b) => !b.disabled);
        const index = buttons.indexOf(event.target.closest('[data-command]'));
        if (index === -1) return;

        const rtl = getComputedStyle(this).direction === 'rtl';
        const target = {
            [rtl ? 'ArrowLeft' : 'ArrowRight']: buttons[(index + 1) % buttons.length],
            [rtl ? 'ArrowRight' : 'ArrowLeft']: buttons[(index - 1 + buttons.length) % buttons.length],
            Home: buttons[0],
            End: buttons.at(-1),
        }[event.key];

        if (target) {
            event.preventDefault();
            this.buttons.forEach((b) => (b.tabIndex = b === target ? 0 : -1));
            target.focus();
        }
    };
}

/** Composer: Enter (or Ctrl/⌘ + Enter) submits the surrounding form. */
export function registerComposer() {
    document.addEventListener('keydown', (event) => {
        const composer = event.target.closest?.('[data-mellow-composer]');
        if (!composer || event.key !== 'Enter' || event.isComposing || !event.target.matches('textarea')) return;

        const mode = composer.dataset.submit;
        const submit = (mode === 'enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey)
            || (mode === 'mod-enter' && (event.ctrlKey || event.metaKey));
        if (!submit) return;

        event.preventDefault();
        if (!event.target.value.trim()) return;

        const form = composer.closest('form');
        if (form) form.requestSubmit();
        else composer.dispatchEvent(new CustomEvent('mellow:submit', { bubbles: true, detail: event.target.value }));
    });
}
